import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageContainer } from '../../components/common/PageContainer/PageContainer.styles';
import { volunteerService } from '../../services/api/volunteerService';
import { toast } from 'react-toastify';
import type { Volunteer } from '../../types/volunteer';
import {
  WhiteBackgroundWrapper,
  ContentWrapper,
  CheckboxContainer,
  VolunteerWrapper,
  CounterValue,
  CounterTitle,
  CounterCard,
  ButtonRow,
  Column,
  FormRow,
  Header,
} from './Volunteer.styles';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';

const Volunteer: React.FC = () => {
  const [formData, setFormData] = useState<Volunteer>({
    id: '',
    status: undefined,
    createdAt: new Date(),
    name: '',
    email: '',
    phone: '',
    education: '',
    interests: [], // Always initialize as empty array
    otherInterest: '',
    hoursAvailable: '',
    language: '',
    proficiency: undefined,
    referralSource: '',
    comments: '',
  });

  const [totalVolunteers, setTotalVolunteers] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();

  // Volunteer count requires admin auth — not available on the public page
  useEffect(() => {
    setTotalVolunteers(0);
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      interests: checked
        ? [...(prevData.interests || []), value]
        : (prevData.interests || []).filter((interest) => interest !== value),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const finalInterests = (formData.interests || []).includes('Other')
      ? [...(formData.interests || []), formData.otherInterest]
      : (formData.interests || []);

    const submissionData = {
      ...formData,
      interests: finalInterests.filter(
        (interest): interest is string => interest !== undefined && interest !== ''
      ),
    };

    try {
      // Check if API endpoint is configured
      if (!import.meta.env.VITE_API_ENDPOINT) {
        toast.error('Service temporarily unavailable. Please try again later.');
        return;
      }

      await volunteerService.createVolunteer(submissionData);
      // Increase counter on successful sign-up
      setTotalVolunteers((prev) => prev + 1);
      // Reset form
      setFormData({
        id: '',
        status: undefined,
        createdAt: new Date(),
        name: '',
        email: '',
        phone: '',
        education: '',
        interests: [],
        otherInterest: '',
        hoursAvailable: '',
        language: '',
        proficiency: undefined,
        referralSource: '',
        comments: '',
      });
      // Redirect to VolunteerSuccess page on successful registration
      navigate('/volunteer/success');
    } catch (error: any) {
      if (error.message.toLowerCase().includes('already exists')) {
        toast.error('A volunteer with this email already exists. Please use a different email.');
      } else {
        toast.error('Failed to create volunteer. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer>
      <WhiteBackgroundWrapper>
        <ContentWrapper style={{ position: 'relative' }}>
          {/* Loading Overlay */}
          {loading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255,255,255,0.7)',
                zIndex: 1000,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <CircularProgress size={60} />
            </Box>
          )}
          <VolunteerWrapper>
            <Header>
              <h1>Volunteer</h1>
              <p>
                Join our mission to make a difference in the lives of others.
                Sign up today and help us transform our community!
              </p>
            </Header>

            <form onSubmit={handleSubmit} style={{ width: '100%' }}>
              <FormRow>
                <Column>
                  <label htmlFor="name">Full Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />

                  <label htmlFor="email">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />

                  <label htmlFor="phone">Phone Number</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                  />

                  <label htmlFor="hoursAvailable">Hours Available per Week</label>
                  <input
                    type="text"
                    id="hoursAvailable"
                    name="hoursAvailable"
                    value={formData.hoursAvailable}
                    onChange={handleChange}
                  />

                  <label htmlFor="language">Preferred Language</label>
                  <input
                    type="text"
                    id="language"
                    name="language"
                    value={formData.language}
                    onChange={handleChange}
                  />

                  <label htmlFor="education">
                    Relevant Educational Achievement/Skills/Experience
                  </label>
                  <textarea
                    id="education"
                    name="education"
                    rows={3}
                    value={formData.education}
                    onChange={handleChange}
                  />
                </Column>

                <Column>
                  <label style={{ fontWeight: 'bold' }}>Areas of Interest</label>
                  <CheckboxContainer>
                    <label>
                      <input
                        type="checkbox"
                        value="Administrative"
                        onChange={handleCheckboxChange}
                        checked={(formData.interests || []).includes('Administrative')}
                      />
                      Administrative
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        value="Palliative/Hospice Home Visits"
                        onChange={handleCheckboxChange}
                        checked={(formData.interests || []).includes('Palliative/Hospice Home Visits')}
                      />
                      Palliative/Hospice Home Visits
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        value="Inpatient Hospice Visits"
                        onChange={handleCheckboxChange}
                        checked={(formData.interests || []).includes('Inpatient Hospice Visits')}
                      />
                      Inpatient Hospice Visits
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        value="Fundraising"
                        onChange={handleCheckboxChange}
                        checked={(formData.interests || []).includes('Fundraising')}
                      />
                      Fundraising
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        value="Other"
                        onChange={handleCheckboxChange}
                        checked={(formData.interests || []).includes('Other')}
                      />
                      Other
                    </label>
                  </CheckboxContainer>

                  {(formData.interests || []).includes('Other') && (
                    <div>
                      <label htmlFor="otherInterest">Please specify your interest</label>
                      <input
                        type="text"
                        id="otherInterest"
                        name="otherInterest"
                        value={formData.otherInterest}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  )}

                  <label htmlFor="proficiency">Proficiency Level</label>
                  <select
                    id="proficiency"
                    name="proficiency"
                    value={formData.proficiency || ''}
                    onChange={handleChange}
                  >
                    <option value="">Select Proficiency</option>
                    <option value="Basic">Basic</option>
                    <option value="Intermediate">Intermediate</option>
                    <option value="Advanced">Advanced</option>
                  </select>

                  <label htmlFor="referralSource">How did you hear about us?</label>
                  <input
                    type="text"
                    id="referralSource"
                    name="referralSource"
                    value={formData.referralSource}
                    onChange={handleChange}
                  />
                  <label htmlFor="comments">Any Additional Comments or Questions</label>
                  <textarea
                    id="comments"
                    name="comments"
                    rows={5}
                    value={formData.comments}
                    onChange={handleChange}
                  />
                </Column>
              </FormRow>

              <ButtonRow>
                <button type="submit" disabled={loading}>
                  {loading ? 'Registering...' : 'Sign Up to Volunteer'}
                </button>
              </ButtonRow>
            </form>

            <CounterCard>
              <CounterTitle>Total Volunteers</CounterTitle>
              <CounterValue>{totalVolunteers.toLocaleString()}</CounterValue>
            </CounterCard>
          </VolunteerWrapper>
        </ContentWrapper>
      </WhiteBackgroundWrapper>
    </PageContainer>
  );
};

export default Volunteer;
